import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FiStar } from 'react-icons/fi';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function TrackingPage() {
  const navigate = useNavigate();
  const [phoneInput, setPhoneInput] = useState('');
  const [activePhone, setActivePhone] = useState('');
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false); 
  
  // 🚀 State สำหรับเช็คว่าเคยเด้ง Popup อัตโนมัติไปแล้วหรือยัง
  const [hasAutoPrompted, setHasAutoPrompted] = useState(false);
  const [storePhone, setStorePhone] = useState('090-993-5049');

  const cleanTechnicianName = (name) => {
    if (!name) return 'ทีมช่างบริษัท';
    // Remove parentheses containing numbers (like phone numbers) or plain phone numbers
    return name.replace(/\(?0[0-9]{1,2}-?[0-9]{3}-?[0-9]{4}\)?/g, '').replace(/\s+/g, ' ').trim();
  };

  useEffect(() => {
    const savedPhone = localStorage.getItem('customerPhone');
    if (savedPhone) {
      setActivePhone(savedPhone);
      setPhoneInput(savedPhone);
      fetchJobs(savedPhone);
    }
  }, []);

  const fetchJobs = async (phoneToSearch) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}?action=track_job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneToSearch })
      });
      const data = await response.json();

      if (data.success) {
        setJobs(data.data);
        if (data.store_phone) {
          setStorePhone(data.store_phone);
        }
        setActivePhone(phoneToSearch);
        localStorage.setItem('customerPhone', phoneToSearch);
      } else {
        setJobs([]);
        if (phoneInput && !activePhone) { 
           Swal.fire('ไม่พบข้อมูล', 'ยังไม่มีประวัติการแจ้งซ่อมจากเบอร์นี้ครับ', 'info');
        }
      }
    } catch (error) {
      Swal.fire('เกิดข้อผิดพลาด', 'ไม่สามารถเชื่อมต่อระบบได้', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // 🧮 สมองกลคำนวณแต้มสะสมจาก MySQL
  const completedJobs = jobs.filter(job => job.status === 'completed' || job.status === 'cancelled');
  const activeJobs = jobs.filter(job => job.status !== 'completed' && job.status !== 'cancelled');
  
  // 🚀 คำนวณจำนวนงานที่ยังไม่ได้รีวิว
  const unratedJobsCount = completedJobs.filter(j => j.status === 'completed' && (!j.rating || parseFloat(j.rating) === 0)).length;

  const totalSpent = completedJobs.reduce((sum, job) => sum + (Number(job.price) || 0), 0);
  const currentPoints = Math.floor(totalSpent / 1000); 

  const isPro = currentPoints >= 10;
  const tierName = isPro ? 'PRO MEMBER' : 'CLASSIC';
  const cardStyle = isPro 
    ? 'from-amber-400 via-yellow-500 to-amber-600 border-amber-300 text-yellow-950 shadow-amber-500/20' 
    : 'from-[#800000] via-[#660000] to-[#3a0002] border-red-500/30 text-amber-100 shadow-red-950/40'; 

  // 🚀 Effect: เด้ง Popup รีวิวอัตโนมัติ (เหมือน Grab)
  useEffect(() => {
    if (jobs.length > 0 && !hasAutoPrompted) {
      // หางานที่เสร็จแล้วแต่ยังไม่รีวิว
      const unratedJob = jobs.find(job => job.status === 'completed' && (!job.rating || parseFloat(job.rating) === 0));
      
      if (unratedJob) {
        setIsHistoryOpen(true); // กางกล่องประวัติออกอัตโนมัติ
        setTimeout(() => {
          handleReview(unratedJob); // สั่งเด้ง Popup อัตโนมัติ
        }, 800); // หน่วงเวลา 0.8 วิให้ UI โหลดเสร็จก่อน
        setHasAutoPrompted(true); // มาร์คไว้ว่าเด้งแล้ว จะได้ไม่เด้งกวนใจซ้ำๆ
      }
    }
  }, [jobs, hasAutoPrompted]);

  const handleManualSearch = (e) => {
    e.preventDefault();
    if (!phoneInput) {
      Swal.fire('แจ้งเตือน', 'กรุณากรอกเบอร์โทรศัพท์ครับ', 'warning');
      return;
    }
    fetchJobs(phoneInput);
  };

  const handleClearPhone = () => {
    localStorage.removeItem('customerPhone');
    setActivePhone('');
    setJobs([]);
    setPhoneInput('');
    setHasAutoPrompted(false); // รีเซ็ตตัวเด้ง popup
  };

  // 🚀 ฟังก์ชัน: Popup รีวิวและให้ดาวสไตล์ Grab
  const handleReview = async (job) => {
    const techName = job.technicianName || 'ทีมช่างของเรา';
    const { value: formValues } = await Swal.fire({
      title: 'ให้คะแนนบริการ',
      html: `
        <p class="text-sm text-gray-500 mb-2">คุณรู้สึกอย่างไรกับการบริการของ <b>${techName}</b>?</p>
        <div class="flex justify-center gap-2 mb-2" id="rating-stars-container">
          <button type="button" class="star-btn text-4xl text-gray-300 grayscale focus:outline-none transition-all hover:scale-110 duration-200" data-value="1">⭐</button>
          <button type="button" class="star-btn text-4xl text-gray-300 grayscale focus:outline-none transition-all hover:scale-110 duration-200" data-value="2">⭐</button>
          <button type="button" class="star-btn text-4xl text-gray-300 grayscale focus:outline-none transition-all hover:scale-110 duration-200" data-value="3">⭐</button>
          <button type="button" class="star-btn text-4xl text-gray-300 grayscale focus:outline-none transition-all hover:scale-110 duration-200" data-value="4">⭐</button>
          <button type="button" class="star-btn text-4xl text-gray-300 grayscale focus:outline-none transition-all hover:scale-110 duration-200" data-value="5">⭐</button>
        </div>
        <div id="rating-label" class="text-center text-sm font-bold text-yellow-600 mb-4 h-6">⭐⭐⭐⭐⭐ ยอดเยี่ยม (5.0)</div>
        <input type="hidden" id="swal-rating" value="5.0">
        <textarea id="swal-review" class="w-full border border-gray-300 p-4 rounded-xl outline-none text-sm shadow-inner" rows="3" placeholder="เขียนคำชมหรือข้อเสนอแนะเพิ่มเติม..."></textarea>
      `,
      showCancelButton: true,
      confirmButtonText: 'ส่งรีวิว',
      cancelButtonText: 'ไว้คราวหน้า',
      confirmButtonColor: '#2563eb',
      didOpen: () => {
        const container = Swal.getHtmlContainer();
        const stars = container.querySelectorAll('.star-btn');
        const ratingInput = container.querySelector('#swal-rating');
        const ratingLabel = container.querySelector('#rating-label');
        
        const labels = {
          '1': '⭐ ปรับปรุง (1.0)',
          '2': '⭐⭐ พอใช้ (2.0)',
          '3': '⭐⭐⭐ ปานกลาง (3.0)',
          '4': '⭐⭐⭐⭐ ดีมาก (4.0)',
          '5': '⭐⭐⭐⭐⭐ ยอดเยี่ยม (5.0)'
        };

        stars.forEach(star => {
          star.addEventListener('click', () => {
            const val = star.getAttribute('data-value');
            ratingInput.value = parseFloat(val).toFixed(1);
            if (ratingLabel) ratingLabel.innerText = labels[val] || '';

            // ไฮไลท์ดาวที่เลือกและล้างสีของดาวดวงถัดไป
            stars.forEach(s => {
              const sVal = s.getAttribute('data-value');
              if (parseInt(sVal) <= parseInt(val)) {
                s.classList.remove('text-gray-300', 'grayscale');
                s.classList.add('text-yellow-400');
              } else {
                s.classList.remove('text-yellow-400');
                s.classList.add('text-gray-300', 'grayscale');
              }
            });
          });
        });

        // ตั้งค่าเริ่มต้น 5 ดาว
        stars[4].click();
      },
      preConfirm: () => {
        return {
          rating: document.getElementById('swal-rating').value,
          review_text: document.getElementById('swal-review').value
        }
      }
    });

    if (formValues) {
      Swal.fire({ title: 'กำลังบันทึก...', didOpen: () => Swal.showLoading() });
      try {
        const response = await fetch(`${API_URL}?action=update_job`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            id: job.id, 
            rating: formValues.rating, 
            review_text: formValues.review_text 
          })
        });
        
        const result = await response.json();
        if(result.success) {
          Swal.fire('ขอบคุณครับ! 🎉', 'รีวิวของคุณจะเป็นกำลังใจให้ทีมงานของเราครับ', 'success');
          setJobs(jobs.map(j => j.id === job.id ? {...j, rating: formValues.rating, review_text: formValues.review_text} : j));
        }
      } catch (err) {
        Swal.fire('ผิดพลาด', 'ไม่สามารถบันทึกรีวิวได้ในขณะนี้', 'error');
      }
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    const [hour, minute] = timeStr.split(':');
    return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  };

  const renderStepper = (status) => {
    const steps = [
      { id: 'pending_approval', label: 'รอประเมิน', icon: '⏳' },
      { id: 'pending', label: 'รอเดินทาง', icon: '📋' },
      { id: 'traveling', label: 'กำลังเดินทาง', icon: '🚗' },
      { id: 'in_progress', label: 'กำลังซ่อม', icon: '🛠️' },
      { id: 'completed', label: 'เสร็จสิ้น', icon: '✅' }
    ];

    let currentIndex = 0;
    if (status === 'pending') currentIndex = 1;
    if (status === 'traveling') currentIndex = 2;
    if (status === 'in_progress') currentIndex = 3;
    if (status === 'completed') currentIndex = 4;

    return (
      <div className="flex items-center justify-between mt-8 relative px-2 py-4 rounded-2xl bg-black/35 border border-white/5 backdrop-blur-md">
        {/* Track Line */}
        <div className="absolute left-6 right-6 top-1/2 -translate-y-4 h-1 bg-white/10 rounded-full z-0"></div>
        <div 
          className="absolute left-6 top-1/2 -translate-y-4 h-1 bg-gradient-to-r from-red-650 to-amber-500 rounded-full z-0 transition-all duration-500 shadow-[0_0_8px_rgba(245,158,11,0.6)]" 
          style={{ width: `calc(${(currentIndex / (steps.length - 1)) * 100}% - 2rem)` }}
        ></div>

        {steps.map((step, index) => {
          const isActive = index <= currentIndex;
          const isCurrent = index === currentIndex;
          return (
            <div key={step.id} className="relative z-10 flex flex-col items-center flex-1">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center border transition-all duration-300 shadow-md ${
                  isCurrent 
                    ? 'border-amber-400 bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 animate-float animate-pulse-gold text-white text-base' 
                    : isActive 
                    ? 'border-red-500 bg-gradient-to-br from-red-600 to-red-800 text-white text-sm shadow-[0_0_8px_rgba(239,68,68,0.4)]' 
                    : 'border-white/10 bg-white/5 text-white/40 text-sm'
                }`}
              >
                <span>{step.icon}</span>
              </div>
              <span className={`text-[9px] mt-2.5 font-bold transition-all duration-300 ${isCurrent ? 'text-amber-400 font-black drop-shadow-[0_0_2px_rgba(245,158,11,0.3)]' : isActive ? 'text-red-400' : 'text-white/40'}`}>{step.label}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderJobCard = (job, isHistory = false) => {
    const isPendingApproval = job.status === 'pending_approval';
    const isRated = job.rating && parseFloat(job.rating) > 0;

    return (
      <div key={job.id} className={`p-6 rounded-3xl border transition-all backdrop-blur-md ${isHistory ? 'bg-white/5 border-white/5 hover:bg-white/10 opacity-90' : 'bg-white/10 border-red-500/25 shadow-xl shadow-red-950/20'}`}>
        <h4 className="font-black text-amber-400 text-lg mb-4 flex items-center gap-2">
          ⚡ {job.problem || 'ไม่ได้ระบุอาการ'}
        </h4>
        
        <div className="bg-black/20 rounded-2xl p-4 flex justify-between items-center text-sm border border-white/5">
           <div>
             <span className="text-white/50 text-[10px] font-bold tracking-wider">ช่างผู้ดูแล:</span>
             <div className="font-bold flex items-center gap-1.5 text-white mt-0.5">
               👨‍🔧 {isPendingApproval ? 'รอยืนยันคิวช่าง' : cleanTechnicianName(job.technicianName)}
             </div>
             {!isPendingApproval && (
               <div className="mt-1">
                 <a href={`tel:${storePhone}`} className="inline-flex items-center gap-1 text-[10px] text-amber-400 font-bold hover:underline">
                   📞 ติดต่อร้าน: {storePhone}
                 </a>
               </div>
             )}
           </div>
           
           <div className="text-right">
              <span className="text-white/50 text-[10px] font-bold tracking-wider">วันที่นัดหมาย:</span>
              {job.appointmentDate ? (
                <div className="font-bold text-amber-400 mt-0.5 text-xs">
                  {new Date(job.appointmentDate).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })}
                  {job.appointmentTime ? ` | ${job.appointmentTime === '09:00' ? 'ช่วงเช้า (09:00 - 12:00 น.)' : job.appointmentTime === '13:00' ? 'ช่วงบ่าย (13:00 - 16:00 น.)' : job.appointmentTime === '16:30' ? 'ช่วงเย็น (16:30 - 18:30 น.)' : formatTime(job.appointmentTime) + ' น.'}` : ' | รอยืนยันเวลา'}
                </div>
              ) : (
                <div className="font-bold text-white/40 mt-0.5 text-xs">รอแอดมินยืนยัน</div>
              )}
           </div>
        </div>

        {!isHistory && renderStepper(job.status)}

        {isHistory && (
          <div className="mt-4 pt-3 border-t border-white/5">
            <div className="flex justify-between items-center mb-3">
               <span className="text-xs text-white/50 font-bold">ราคาสุทธิ</span>
               <span className="font-black text-amber-400 text-lg">{job.price ? `${Number(job.price).toLocaleString()} ฿` : '-'}</span>
            </div>
            
            {/* โซนรีวิว (เฉพาะงานที่จบแล้ว) */}
            {job.status === 'completed' && (
              <div className="bg-black/20 p-3 rounded-2xl border border-white/5">
                {!isRated ? (
                  <button onClick={() => handleReview(job)} className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 hover:from-amber-500 hover:to-amber-700 text-yellow-950 font-black py-3 rounded-xl transition shadow-md text-sm animate-pulse cursor-pointer border border-amber-300">
                    <FiStar className="fill-current" /> ประเมินการบริการเลย!
                  </button>
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-white/55 font-bold">คะแนนของคุณ:</span>
                      <span className="text-amber-400 flex items-center gap-1 text-sm font-black">
                        <FiStar className="fill-current" /> {job.rating}
                      </span>
                    </div>
                    {job.review_text && (
                      <p className="text-xs text-white/80 italic bg-white/5 p-2.5 rounded-lg border border-white/5">"{job.review_text}"</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const savedPhone = activePhone;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#120000] via-[#260105] to-[#0a0001] flex justify-center p-4 font-sans text-white relative overflow-hidden">
      
      {/* 🔮 3D Glowing Blurred Background Orbs */}
      <div className="absolute top-[-10%] left-[-15%] w-[60%] aspect-square rounded-full bg-red-800/10 blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-[-10%] right-[-15%] w-[60%] aspect-square rounded-full bg-amber-500/5 blur-[100px] pointer-events-none"></div>
      
      {/* 🚀 Custom 3D & Glassmorphic CSS Injections */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-6px) rotate(2deg); }
        }
        @keyframes pulse-gold {
          0%, 100% { box-shadow: 0 0 5px rgba(245,158,11,0.4), 0 0 10px rgba(245,158,11,0.1); }
          50% { box-shadow: 0 0 12px rgba(245,158,11,0.7), 0 0 24px rgba(245,158,11,0.35); }
        }
        .animate-float {
          animation: float 3.5s ease-in-out infinite;
        }
        .animate-pulse-gold {
          animation: pulse-gold 2.5s infinite;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.04);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
      `}</style>

      <div className="w-full max-w-md space-y-6 relative z-10">
        
        {/* Header */}
        <div className="text-center mt-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-500/10 border border-red-500/20 text-[10px] text-red-400 font-black rounded-full uppercase tracking-wider mb-2">
            🛡️ THUNDER SECURITY SYSTEM
          </div>
          <h1 className="text-2xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-white via-amber-200 to-amber-400">
            ระบบเช็คสถานะ & บัตรสมาชิก
          </h1>
          <p className="text-amber-500/70 text-xs font-bold mt-1">THUNDER MAXHOME</p>
        </div>

        {!savedPhone ? (
          <div className="space-y-6 animate-fade-in">
            {/* โซนบัตรสมาชิก (แบบปัดซ้ายขวา) */}
            <div className="w-full overflow-hidden mb-2">
              <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 px-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                
                {/* 1. บัตร Classic (สีแดงกำมะหยี่) */}
                <div className="snap-center shrink-0 w-[85%] bg-gradient-to-br from-[#800000] via-[#5d0205] to-[#330002] border border-red-500/20 rounded-3xl p-6 text-amber-100 shadow-xl relative overflow-hidden">
                  <div className="absolute right-[-20px] bottom-[-20px] w-40 h-40 bg-red-500/5 rounded-full blur-xl pointer-events-none"></div>
                  <div className="flex justify-between items-start">
                    <p className="text-[9px] font-black tracking-widest text-amber-400/80">THUNDER MAXHOME MEMBER</p>
                    <span className="text-xs">🛡️</span>
                  </div>
                  <h2 className="text-xl font-black italic tracking-widest mt-1 text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-amber-500">CLASSIC</h2>
                  <div className="mt-8 flex items-end gap-2">
                    <span className="text-5xl font-black text-white">0</span>
                    <span className="text-xs font-bold text-amber-400/85 mb-1.5">คะแนน</span>
                  </div>
                  <div className="mt-4 bg-white/5 rounded-xl p-3 text-center text-[10px] font-bold border border-white/5 text-amber-200/90 backdrop-blur-sm">
                    🎁 สะสม 10 แต้ม แลกบริการฟรี 1 ครั้ง
                  </div>
                </div>

                {/* 2. บัตร PRO (สีทอง) */}
                <div className="snap-center shrink-0 w-[85%] bg-gradient-to-br from-[#d4af37] via-[#e5c158] to-[#b8860b] border border-amber-300/40 rounded-3xl p-6 text-yellow-950 shadow-xl relative overflow-hidden">
                  <div className="absolute right-[-20px] bottom-[-20px] w-40 h-40 bg-white/10 rounded-full blur-xl pointer-events-none"></div>
                  <div className="flex justify-between items-start">
                    <p className="text-[9px] font-black tracking-widest text-yellow-900/80">THUNDER MAXHOME MEMBER</p>
                    <span className="text-xs">👑</span>
                  </div>
                  <h2 className="text-xl font-black italic tracking-widest mt-1 text-yellow-950">PRO TIER</h2>
                  <div className="mt-8 flex items-end gap-2">
                     <span className="text-5xl font-black text-yellow-950/80">🔒</span>
                     <span className="text-xs font-black text-yellow-900/80 mb-2">ยังไม่ปลดล็อก</span>
                  </div>
                  <div className="mt-4 bg-black/10 rounded-xl p-3 text-center text-[10px] font-bold border border-black/10 text-yellow-950">
                    ⚡ สิทธิพิเศษส่วนลด และบริการระดับ VIP
                  </div>
                </div>

              </div>
              <p className="text-center text-[9px] text-white/40 mt-1">← ปัดซ้ายขวาเพื่อดูระดับสมาชิก →</p>
              <p className="text-center text-[9px] text-white/40">ทุกยอดใช้จ่าย 1,000 บาท รับ 1 คะแนน</p>
            </div>

            {/* ฟอร์มกรอกเบอร์โทร (Glassmorphism) */}
            <div className="glass-card p-6 rounded-3xl shadow-2xl relative overflow-hidden border border-white/10">
              <form onSubmit={handleManualSearch} className="space-y-4">
                <div>
                  <label className="block text-center text-xs font-bold text-amber-400 mb-2">กรอกเบอร์มือถือที่ลงทะเบียนรับบริการ</label>
                  <input 
                    type="tel" 
                    required 
                    className="w-full bg-black/40 text-center text-xl tracking-widest font-black text-white border border-white/10 p-4 rounded-2xl focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none transition duration-200 placeholder-white/20" 
                    placeholder="08X-XXX-XXXX" 
                    value={phoneInput} 
                    onChange={e => setPhoneInput(e.target.value)} 
                  />
                </div>
                <button 
                  type="submit" 
                  disabled={isLoading} 
                  className="w-full bg-gradient-to-r from-red-700 via-red-600 to-red-700 hover:from-red-600 hover:to-red-800 text-white font-black py-4 rounded-2xl shadow-lg shadow-red-950/30 transition-all border border-red-500/20 active:scale-98 cursor-pointer"
                >
                  {isLoading ? 'กำลังค้นหา...' : '🔍 ตรวจสอบคิวงาน / เข้าใช้งาน'}
                </button>
              </form>
              <div className="mt-5 text-center">
                <button onClick={() => navigate('/booking')} className="text-amber-400 text-xs font-bold underline hover:text-amber-300">
                  ← กลับไปหน้าทำรายการจองช่าง
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            
            {/* บัตรสมาชิกตามคะแนนจริง */}
            <div className={`bg-gradient-to-br ${cardStyle} rounded-3xl p-6 shadow-xl relative overflow-hidden transition-all duration-500 border`}>
              <div className="absolute right-[-20px] bottom-[-20px] w-42 h-42 bg-white/5 rounded-full blur-xl pointer-events-none"></div>
              <div className="flex justify-between items-start relative z-10">
                <div>
                  <p className="text-[9px] font-black tracking-widest opacity-80">THUNDER MAXHOME MEMBER</p>
                  <h2 className="text-2xl font-black italic tracking-widest mt-1 transition-all duration-300">{tierName}</h2>
                </div>
                <button 
                  onClick={handleClearPhone} 
                  className="bg-black/35 hover:bg-black/50 text-white text-[10px] px-3.5 py-1 rounded-full backdrop-blur-sm border border-white/10 transition cursor-pointer"
                >
                  ออกจากระบบ
                </button>
              </div>
              <div className="mt-8 relative z-10 flex items-end gap-1.5">
                <span className="text-5xl font-black tracking-tight">{currentPoints}</span>
                <span className="text-xs font-bold opacity-80 mb-2">คะแนนสะสม</span>
              </div>
              <div className="mt-4 bg-white/5 dark:bg-black/10 rounded-xl p-3 text-center text-[10px] font-bold backdrop-blur-sm relative z-10 border border-white/5">
                {isPro ? '✨ สิทธิ์สมาชิกพรีเมียม VIP: รับบริการพิเศษและส่วนลดแล้ว! ✨' : '🎁 สะสมครบ 10 คะแนน แลกบริการล้างแอร์ฟรี 1 ครั้ง'}
              </div>
            </div>
            
            <p className="text-center text-[9px] text-white/40">ทุกยอดใช้จ่าย 1,000 บาท = 1 คะแนน (ยอดรวมใช้บริการ: {totalSpent.toLocaleString()} บาท)</p>

            {/* โซนการติดตามสถานะงานที่กำลังรอดำเนินการ */}
            <div className="space-y-4">
              <h3 className="text-xs font-bold text-amber-400 tracking-wider uppercase border-l-2 border-red-500 pl-2">
                📋 ติดตามงานบริการที่กำลังทำอยู่ (Active Tracking)
              </h3>
              {isLoading ? (
                <div className="text-center py-8 text-white/50 font-bold animate-pulse">กำลังโหลดสถานะคิวงาน...</div>
              ) : activeJobs.length > 0 ? (
                <div className="space-y-4">
                  {activeJobs.map(job => renderJobCard(job, false))}
                </div>
              ) : (
                <div className="glass-card p-6 rounded-3xl text-center border border-white/5">
                  <p className="text-white/45 font-bold text-xs">คุณไม่มีคิวงานที่กำลังทำอยู่ในขณะนี้</p>
                </div>
              )}
            </div>

            {/* ประวัติการใช้บริการ */}
            {completedJobs.length > 0 && (
              <div className="glass-card rounded-3xl border border-white/5 overflow-hidden transition-all shadow-lg">
                <button 
                  onClick={() => setIsHistoryOpen(!isHistoryOpen)} 
                  className="w-full px-6 py-4 flex justify-between items-center bg-white/5 hover:bg-white/10 transition text-left cursor-pointer"
                >
                  <div className="font-bold text-white flex items-center gap-2 text-xs">
                    📜 ประวัติการรับบริการในอดีต ({completedJobs.length} งาน)
                    {unratedJobsCount > 0 && (
                      <span className="bg-red-600 text-white text-[9px] px-2 py-0.5 rounded-full animate-pulse font-black border border-red-500 shadow-md">
                        ⭐ รอรีวิว {unratedJobsCount} งาน
                      </span>
                    )}
                  </div>
                  <span className={`text-[10px] text-amber-400 transform transition-transform duration-300 ${isHistoryOpen ? 'rotate-180' : ''}`}>▼</span>
                </button>
                
                {isHistoryOpen && (
                  <div className="p-4 bg-black/20 border-t border-white/5 space-y-4">
                    {completedJobs.map(job => renderJobCard(job, true))}
                  </div>
                )}
              </div>
            )}

            {/* ปุ่มแจ้งซ่อมใหม่ (ธีมแดง) */}
            <div className="pb-10 pt-2">
              <button 
                onClick={() => navigate('/booking')} 
                className="w-full bg-gradient-to-r from-red-700 via-red-600 to-red-700 hover:from-red-650 hover:to-red-750 text-white font-black py-4 rounded-2xl transition duration-200 border border-red-500/20 shadow-md shadow-red-950/30 cursor-pointer text-xs"
              >
                + แจ้งซ่อมหรือจองคิวบริการใหม่
              </button>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}